import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, C, fmtChips } from "../../lib/api";
import { Card, Stakes, Stat, fmtNum } from "./ui";

const tick = { fill: "#94a3b8", fontSize: 10 };
const tip = { contentStyle: { background: "#0b0b10", border: "1px solid rgba(214,170,80,0.4)", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#e6b94c" } };

const DayChart = ({ data, dataKey, color, name, money }) => (
  <ResponsiveContainer width="100%" height={180}>
    <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
      <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
      <XAxis dataKey="day" tick={tick} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
      <YAxis tick={tick} axisLine={false} tickLine={false} allowDecimals={false} />
      <Tooltip {...tip} formatter={(v) => [money ? fmtChips(v) : fmtNum(v), name]} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
      <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

// Stakes ranked by hands in the last 7 days: bar = share of this week's hands, with all-time hands, players, rake and seats now.
const Popularity = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.hands_week));
  if (!rows.length) return <div className="text-slate-500 text-xs">No hands played yet.</div>;
  return (
    <div className="flex flex-col gap-3" data-testid="popularity-list">
      {rows.map((r, i) => (
        <div key={r.stakes} data-testid={`popularity-${r.stakes}`} className="grid grid-cols-12 gap-3 items-center text-sm">
          <div className="col-span-2 flex items-center gap-2"><span className="text-[10px] text-slate-500 w-4">{i + 1}</span><Stakes sb={r.stakes.split("/")[0]} bb={r.stakes.split("/")[1]} className="text-amber-100" raw /></div>
          <div className="col-span-6">
            <div className="h-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.hands_week / max) * 100}%`, background: "linear-gradient(90deg,#c9962e,#f1cf6a)", transition: "width 600ms" }} /></div>
            <div className="text-[10px] text-slate-400 mt-1">{fmtNum(r.hands_week)} hands this week · {fmtNum(r.hands)} all time · {r.games.map((g) => C.GAME_RULES[g]?.label?.split(" ").pop() || g).join(", ")}</div>
          </div>
          <div className="col-span-2 text-right"><div className="font-mono">{fmtNum(r.players)}</div><div className="text-[10px] text-slate-400">players</div></div>
          <div className="col-span-2 text-right"><div className="font-mono text-amber-200">{fmtChips(r.rake)}</div><div className="text-[10px] text-slate-400">rake{r.seated_now ? ` · ${r.seated_now} seated now` : ""}</div></div>
        </div>
      ))}
    </div>
  );
};

// Growth + money at a glance: rake, hands, sign-ups and active players, with 14-day charts.
export const Overview = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get(C.ApiRoutes.ADMIN_STATS).then((r) => setS(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!s) return <div className="text-slate-400 text-sm" data-testid="admin-loading">Loading…</div>;
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6" data-testid="admin-overview">
      <Card title="Rake" className="xl:col-span-4" testId="rake-card">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Today" value={fmtChips(s.rake.today)} testId="rake-today" />
          <Stat label="7 days" value={fmtChips(s.rake.week)} testId="rake-week" />
          <Stat label="All time" value={fmtChips(s.rake.all)} testId="rake-all" />
        </div>
        <div className="mt-4"><DayChart data={s.rake_per_day} dataKey="total" color="#e6b94c" name="Rake" money /></div>
      </Card>
      <Card title="Players" className="xl:col-span-4" testId="players-card">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Registered" value={fmtNum(s.players_total)} sub={`+${s.signups.today} today · +${s.signups.week} this week`} testId="players-total" />
          <Stat label="Active 24h" value={fmtNum(s.active.day)} testId="active-day" />
          <Stat label="Active 7d" value={fmtNum(s.active.week)} testId="active-week" />
        </div>
        <div className="mt-4"><DayChart data={s.signups_per_day} dataKey="n" color="#38bdf8" name="Sign-ups" /></div>
      </Card>
      <Card title="Hands" className="xl:col-span-4" testId="hands-card">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Today" value={fmtNum(s.hands.today)} testId="hands-today" />
          <Stat label="7 days" value={fmtNum(s.hands.week)} testId="hands-week" />
          <Stat label="Seated now" value={fmtNum(s.seated_now)} sub={`${s.open_tables} open table${s.open_tables === 1 ? "" : "s"}`} testId="seated-now" />
        </div>
        <div className="mt-4"><DayChart data={s.hands_per_day} dataKey="n" color="#34d399" name="Hands" /></div>
      </Card>
      <Card title="Sit N Go fees (kept separate from rake)" className="xl:col-span-12" testId="sng-fees-card">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <Stat label="Fees today" value={fmtChips(s.sng_fees.today)} testId="sng-fees-today" />
          <Stat label="Fees 7 days" value={fmtChips(s.sng_fees.week)} testId="sng-fees-week" />
          <Stat label="Fees all time" value={fmtChips(s.sng_fees.all)} testId="sng-fees-all" />
          <Stat label="SNGs today" value={fmtNum(s.tournaments.today)} />
          <Stat label="SNGs 7 days" value={fmtNum(s.tournaments.week)} />
          <Stat label="SNGs all time" value={fmtNum(s.tournaments.all)} />
        </div>
      </Card>
      <Card title="Most played stakes" className="xl:col-span-6" testId="popularity-card">
        <Popularity rows={s.popularity} />
      </Card>
      <Card title="Rake by table" className="xl:col-span-6" testId="rake-by-table">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-400 text-left"><tr><th className="py-2">Table</th><th>Raked hands</th><th className="text-right">Rake</th></tr></thead>
          <tbody>
            {s.rake_by_table.map((r) => (
              <tr key={r.table_id} className="border-t border-white/5" data-testid={`rake-row-${r.table_id}`}>
                <td className="py-2"><div>{r.name}</div><div className="font-mono text-[10px] text-slate-500">{r.table_id}</div></td><td>{fmtNum(r.hands)}</td><td className="text-right font-mono text-amber-200">{fmtChips(r.total)}</td>
              </tr>
            ))}
            {!s.rake_by_table.length && <tr><td colSpan={3} className="py-3 text-slate-500 text-xs">No rake taken yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
